<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('expensePosting.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('expensePosting.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="d-flex ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">{{
              $t('add_new')
            }}</ts-button>
            <a-range-picker
              v-model="dateRange"
              :ranges="{
                'This Month': [moment().startOf('M'), moment().endOf('month')]
              }"
              @change="fetchData()"
              class="ts-w-64"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
            />
          </div>
          <div class="d-flex ts-justify-end ts-space-x-3">
            <Tooltip
              :content="$t('expensePosting.search')"
              placement="top"
              transfer
              :max-width="width"
            >
              <Input
                search
                v-model="search"
                :placeholder="$t('expensePosting.search')"
                :style="width"
                @on-focus="() => (width = 'width: 500px')"
                @on-blur="() => (width = 'width: 300px')"
              />
            </Tooltip>
            <ts-button
              v-tooltip="$t('filter')"
              @click.prevent="onFilter"
              style="border: 1px black solid;height: 32px !important"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
            <Drawer
              :title="$t('filter')"
              :closable="false"
              :value="visible"
              @on-close="() => (visible = false)"
              width="300px"
            >
              <div class="mb-3">
                <label class="form-col-label control-label">{{
                  $t('branchName')
                }}</label>
                <ts-branch-filter-v2
                  @filter="branchFilter"
                  class="ts-w-full"
                ></ts-branch-filter-v2>
              </div>
              <div class="mb-3">
                <label class="form-col-label control-label">
                  {{ $t('expensePosting.expenseType') }}
                </label>
                <a-select
                  v-model="expenseType"
                  mode="multiple"
                  :options="expenseTypes"
                  :placeholder="$t('all')"
                  class="ts-w-full"
                ></a-select>
              </div>
              <div class="mb-3">
                <label class="form-col-label control-label">
                  {{ $t('expensePosting.expenseBy') }}
                </label>
                <a-select
                  v-model="employeeId"
                  mode="multiple"
                  showSearch
                  :options="employees"
                  :placeholder="$t('all')"
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  class="ts-w-full"
                ></a-select>
              </div>
              <Spin size="large" fix v-if="loading_filter"></Spin>
            </Drawer>
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        highlight-row
        stripe
        max-height="700"
      >
        <template slot-scope="{ row }" slot="branch">
          {{ row.branch ? row.branch.branch_name_en : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="cash_advance">
          <span>
            {{
              row.cash_advance
                ? row.cash_advance.advance_no + ' | ' + row.cash_advance.purpose
                : 'N/A'
            }}
          </span>
          <br />
          <div v-if="row.cash_advance" class="ts-flex">
            <ts-display-currency
              :amount="row.cash_advance.advance_amount"
              :currency_id="row.cash_advance.currency_id"
            />
          </div>
        </template>
        <template slot-scope="{ row }" slot="reimburse_by">
          {{
            row.reimburse_by_emp ? row.reimburse_by_emp.employee_name_en : 'N/A'
          }}
        </template>
        <template slot-scope="{ row }" slot="expense_amount">
          <ts-display-currency
            :amount="row.grand_total"
            :currency_id="row.currency_id"
          />
        </template>
        <template slot-scope="{ row }" slot="reimburse_amount">
          <div
            class="text-right ts-text-blue-700 ts-font-semibold"
            v-if="row.reimburse_amount"
          >
            <ts-display-currency
              :amount="row.reimburse_amount"
              :currency_id="row.currency_id"
            />
          </div>
          <div class="text-right ts-text-blue-700 ts-font-semibold" v-else>
            N/A
          </div>
        </template>
        <template slot-scope="{ row }" slot="reimbursed_by">
          {{
            row.reimburse_by_emp ? row.reimburse_by_emp.employee_name_en : null
          }}
        </template>
        <template slot-scope="{ row }" slot="payment_method">
          <span>
            {{
              row.payment_method ? row.payment_method.payment_method : null
            }} </span
          ><br />
          <span v-if="row.payment_method_id != 1">
            <span> {{ row.bank_name }} : </span>
            <span>
              {{ row.account_name + ' (' + row.account_number + ')' }}
            </span>
          </span>
        </template>
        <template slot-scope="{ row }" slot="created_by">
          {{ row.created_by_emp ? row.created_by_emp.employee_name_en : null }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <!-- <a
            href="#"
            v-tooltip="$t('dailyCashAdvance.reprint')"
            class="text-red mr-2"
            @click.prevent="reprintReport(row)"
          >
            <i class="fas fa-file-pdf"></i>
          </a> -->
          <a
            href="#"
            v-tooltip="$t('edit')"
            class="text-primary mr-2"
            @click.prevent="edit(row)"
          >
            <i class="fas fa-edit"></i>
          </a>
          <a-popconfirm
            placement="leftTop"
            :title="$t('are_you_sure_to_delete')"
            :ok-text="$t('yes')"
            :cancel-text="$t('no')"
            @confirm="deleteRecord(row)"
          >
            <a href="#" :disabled="row._deleting">
              <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
              <i class="fa fa-trash text-red" v-else></i>
            </a>
          </a-popconfirm>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import expandRow from './table-expand.vue'
import { Errors } from 'form-backend-validation'

export default {
  name: 'expensePostingIndex',
  data() {
    return {
      loading: false,
      waiting: false,
      show_pdf: false,
      visible: false,
      loading_filter: false,
      src: '',
      width: 'width: 300px',
      errors: new Errors(),
      employeeLists: []
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('expense/expensePosting', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.expense.expensePosting.search
      },
      set(newValue) {
        this.$store.commit('expense/expensePosting/SET_SEARCH', newValue)
        this.$store.commit('expense/expensePosting/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get() {
        return this.$store.state.expense.expensePosting.dateRange
      },
      set(newValue) {
        this.$store.commit('expense/expensePosting/SET_DATE_RANGE', newValue)
      }
    },
    expenseType: {
      get() {
        return this.$store.state.expense.expensePosting.expenseType
      },
      set(newValue) {
        this.$store.commit('expense/expensePosting/SET_EXPENSE_TYPE', newValue)
      }
    },
    employeeId: {
      get() {
        return this.$store.state.expense.expensePosting.employeeId
      },
      set(newValue) {
        this.$store.commit('expense/expensePosting/SET_EMPLOYEE_ID', newValue)
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                row: params.row
              }
            })
          },
          width: 30
        },
        {
          title: this.$t('expensePosting.date'),
          key: 'exp_date',
          width: 120,
          sortable: true
        },
        {
          title: this.$t('expensePosting.branch'),
          slot: 'branch',
          width: 120,
          sortable: true
        },
        {
          title: this.$t('expensePosting.expenseNo'),
          key: 'expense_no',
          width: 150,
          sortable: true
        },
        {
          title: this.$t('expensePosting.expenseType'),
          key: 'expense_type_name',
          width: 150,
          sortable: true
        },
        {
          title: this.$t('expensePosting.cashAdvance'),
          slot: 'cash_advance',
          width: 240,
          sortable: true,
          tooltip: true
        },
        {
          title: this.$t('expensePosting.reimburseBy'),
          slot: 'reimburse_by',
          width: 150,
          sortable: true
        },
        {
          title: this.$t('expensePosting.expenseAmount'),
          slot: 'expense_amount',
          width: 200,
          align: 'right',
          sortable: true
        },
        {
          title: this.$t('expensePosting.paymentMethod'),
          slot: 'payment_method',
          width: 210,
          sortable: true,
          tooltip: true
        },
        {
          title: this.$t('expensePosting.createdBy'),
          slot: 'created_by',
          width: 200,
          sortable: true
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 120
        }
      ]
    },
    expenseTypes() {
      return [
        {
          label: 'General',
          value: 0
        },
        {
          label: 'Personal Cash',
          value: 1
        },
        {
          label: 'Cash Advance',
          value: 2
        }
      ]
    },
    employees() {
      if (this.employeeLists) {
        return this.employeeLists.map(el => ({
          value: el.employee_id,
          label: el.employee_name_en + '|' + el.employee_name_kh
        }))
      }
      return []
    }
  },
  methods: {
    ...mapActions('expense/expensePosting', ['getFormViewModel']),
    moment,
    branchFilter(value) {
      this.$store.commit('expense/expensePosting/SET_BRANCH_ID', value)
      this.fetchData()
    },
    async onFilter() {
      this.loading_filter = true
      this.visible = true
      await this.getFormViewModel({
        index: 'employees',
        params: {
          fnName: 'employees'
        }
      })
        .then(response => {
          this.employeeLists = response.data.employees
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading_filter = false))
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('expense/expensePosting/fetch', { ...attributes })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.$router.push({ name: 'expense-posting-create' })
    },
    edit(record) {
      this.$router.push({
        name: 'expense-posting-edit',
        params: { id: record.exp_main_id }
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('expense/expensePosting/destroy', record.exp_main_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    reprintReport(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('expense/expensePosting/toPdf', record.exp_main_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500),
    expenseType: debounce(function() {
      this.fetchData()
    }, 500),
    employeeId: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('expense/expensePosting/RESET_STATE')
    next()
  }
}
</script>
